import React, { useState } from "react";
import { X, Copy, Check, Globe } from "lucide-react";
import { API } from "../lib/api";

const CASHAPP = "$JNX1112";
const ZELLE = "thinknoutdabox1@gmail.com";

export default function AlbumSupportModal({ open, onClose }) {
  const [amount, setAmount] = useState("10");
  const [email, setEmail] = useState("");
  const [method, setMethod] = useState("Cash App");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const [copied, setCopied] = useState("");

  if (!open) return null;

  const amt = parseFloat(amount);
  const validAmt = !isNaN(amt) && amt >= 10;
  const validEmail = /\S+@\S+\.\S+/.test(email);

  const reset = () => { setSent(false); setError(""); setBusy(false); };
  const close = () => { reset(); onClose(); };

  const copy = (text, key) => {
    try {
      const p = navigator.clipboard?.writeText(text);
      if (p && p.then) p.then(() => { setCopied(key); setTimeout(() => setCopied(""), 1500); }).catch(() => {});
      else { setCopied(key); setTimeout(() => setCopied(""), 1500); }
    } catch (e) {
      // clipboard unavailable — ignore silently
    }
  };

  const handleManual = async () => {
    setError("");
    if (!validAmt) return setError("Minimum payment is $10.");
    if (!validEmail) return setError("Enter a valid email so we can send your download.");
    setBusy(true);
    try {
      const res = await fetch(`${API}/album/manual-notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ amount: amt, email, method }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.detail || "Could not send");
      setSent(true);
    } catch (e) {
      setError(e.message);
    }
    setBusy(false);
  };

  return (
    <div
      data-testid="album-support-modal"
      className="fixed inset-0 z-[200] flex items-center justify-center px-4 py-8 overflow-y-auto"
      style={{ background: "rgba(21,16,14,0.88)" }}
      onClick={close}
    >
      <div
        className="relative w-full max-w-md bg-[#15100E] text-[#ECE7DB] border-2 border-[#CF9026] edge"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="album-support-close"
          onClick={close}
          className="absolute top-3 right-3 h-9 w-9 flex items-center justify-center border-2 border-[#ECE7DB]/30 hover:border-[#CF9026] hover:text-[#CF9026] transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        <div className="p-6 sm:p-8">
          <p className="font-tech text-[#CF9026] tracking-[0.3em] text-[10px] flex items-center gap-2">
            <Globe size={13} /> INTERNATIONAL
          </p>
          <h2 className="font-display font-extrabold stretch-text text-4xl sm:text-5xl leading-[0.85] mt-2">NAME YOUR PRICE</h2>

          {sent ? (
            <div data-testid="album-support-sent" className="mt-6 animate-fade-up">
              <div className="flex items-center gap-2 text-[#CF9026] font-tech tracking-[0.2em] text-sm">
                <Check size={18} /> GOT IT
              </div>
              <p className="font-body text-sm text-[#ECE7DB]/80 leading-relaxed mt-3">
                Send <span className="text-[#CF9026] font-bold">${amt.toFixed(2)}</span> via {method}, then
                Jameel will personally email your album download link to <span className="text-[#CF9026]">{email}</span>.
              </p>
              <div className="mt-5 space-y-3">
                <div className="flex items-center justify-between border-2 border-[#ECE7DB]/25 px-4 py-3">
                  <div><p className="font-tech text-[9px] tracking-[0.25em] text-[#ECE7DB]/50">CASH APP</p><p className="font-display font-bold text-lg">{CASHAPP}</p></div>
                  <button data-testid="copy-cashapp" onClick={() => copy(CASHAPP, "cashapp")} className="text-[#CF9026] hover:scale-110 transition-transform">{copied === "cashapp" ? <Check size={18} /> : <Copy size={18} />}</button>
                </div>
                <div className="flex items-center justify-between border-2 border-[#ECE7DB]/25 px-4 py-3">
                  <div><p className="font-tech text-[9px] tracking-[0.25em] text-[#ECE7DB]/50">ZELLE</p><p className="font-display font-bold text-base break-all">{ZELLE}</p></div>
                  <button data-testid="copy-zelle" onClick={() => copy(ZELLE, "zelle")} className="text-[#CF9026] hover:scale-110 transition-transform shrink-0 ml-2">{copied === "zelle" ? <Check size={18} /> : <Copy size={18} />}</button>
                </div>
              </div>
              <button data-testid="album-support-done" onClick={close} className="mt-6 w-full font-tech tracking-[0.25em] text-xs bg-[#ECE7DB] text-[#15100E] py-3 hover:bg-[#CF9026] transition-colors">DONE</button>
            </div>
          ) : (
            <>
              <p className="font-body text-sm text-[#ECE7DB]/70 leading-relaxed mt-3">
                Pay what you want — minimum <span className="text-[#CF9026] font-bold">$10</span> — via Cash App or Zelle.
                After you send it, Jameel personally emails your album download link.
              </p>

              <div className="mt-6 space-y-4">
                <div>
                  <label className="font-tech text-[9px] tracking-[0.25em] text-[#ECE7DB]/50 block mb-1">YOUR PRICE (USD)</label>
                  <div className="flex items-center border-2 border-[#ECE7DB]/30 focus-within:border-[#CF9026] transition-colors">
                    <span className="px-3 font-display font-extrabold text-2xl text-[#CF9026]">$</span>
                    <input
                      data-testid="album-support-amount"
                      type="number" min="10" step="1" value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="w-full bg-transparent py-3 pr-4 font-display font-extrabold text-2xl outline-none"
                      placeholder="10"
                    />
                  </div>
                </div>
                <div>
                  <label className="font-tech text-[9px] tracking-[0.25em] text-[#ECE7DB]/50 block mb-1">EMAIL (FOR YOUR DOWNLOAD)</label>
                  <input
                    data-testid="album-support-email"
                    type="email" value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full bg-transparent py-3 px-4 border-2 border-[#ECE7DB]/30 focus:border-[#CF9026] outline-none font-body text-sm transition-colors"
                    placeholder="you@email.com"
                  />
                </div>
              </div>

              <div className="flex items-center gap-3 my-5">
                <div className="h-px flex-1 bg-[#ECE7DB]/20" />
                <span className="font-tech text-[9px] tracking-[0.25em] text-[#ECE7DB]/40">SEND PAYMENT VIA</span>
                <div className="h-px flex-1 bg-[#ECE7DB]/20" />
              </div>

              <div className="space-y-3">
                <div className="flex items-center justify-between border-2 border-[#ECE7DB]/25 px-4 py-2.5">
                  <div><p className="font-tech text-[9px] tracking-[0.25em] text-[#ECE7DB]/50">CASH APP</p><p className="font-display font-bold text-lg">{CASHAPP}</p></div>
                  <button data-testid="copy-cashapp-form" onClick={() => copy(CASHAPP, "cashapp")} className="text-[#CF9026] hover:scale-110 transition-transform">{copied === "cashapp" ? <Check size={18} /> : <Copy size={18} />}</button>
                </div>
                <div className="flex items-center justify-between border-2 border-[#ECE7DB]/25 px-4 py-2.5">
                  <div className="min-w-0"><p className="font-tech text-[9px] tracking-[0.25em] text-[#ECE7DB]/50">ZELLE</p><p className="font-display font-bold text-sm break-all">{ZELLE}</p></div>
                  <button data-testid="copy-zelle-form" onClick={() => copy(ZELLE, "zelle")} className="text-[#CF9026] hover:scale-110 transition-transform shrink-0 ml-2">{copied === "zelle" ? <Check size={18} /> : <Copy size={18} />}</button>
                </div>
              </div>

              <div className="flex gap-2 mt-4">
                {["Cash App", "Zelle"].map((m) => (
                  <button
                    key={m}
                    data-testid={`album-method-${m.replace(/\s+/g, "-").toLowerCase()}`}
                    onClick={() => setMethod(m)}
                    className={`flex-1 font-tech text-[10px] tracking-[0.15em] py-2 border-2 transition-colors ${method === m ? "bg-[#ECE7DB] text-[#15100E] border-[#ECE7DB]" : "border-[#ECE7DB]/30 text-[#ECE7DB]/60 hover:border-[#CF9026]"}`}
                  >
                    {m.toUpperCase()}
                  </button>
                ))}
              </div>

              {error && <p data-testid="album-support-error" className="mt-3 font-tech text-[11px] tracking-wide text-[#ff6b6b]">{error}</p>}

              <button
                data-testid="album-support-manual"
                disabled={busy}
                onClick={handleManual}
                className="mt-4 w-full font-tech tracking-[0.2em] text-xs bg-[#7A1623] text-[#ECE7DB] py-4 hover:bg-[#CF9026] hover:text-[#15100E] transition-colors disabled:opacity-50"
              >
                {busy ? "SENDING…" : "I'VE SENT IT — NOTIFY JNX"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
